/**
 * Material identity on the map: a hue AND a dash pattern, never a hue alone.
 *
 * Why the dash is not decoration
 * ------------------------------
 * A map is an all-pairs colour problem. In a bar chart only neighbouring bars need to be
 * told apart, but here any two routes can touch, so every pair of materials has to clear the
 * separation floors — and under that rule this palette tops out at FOUR hues. Validated with
 * the dataviz validator (`--pairs all`), not eyeballed:
 *
 *   light (#2a78d6, #eda100, #e87ba4, #008300) on #fcfcfb — ALL CHECKS PASS
 *     worst pair CVD ΔE 13.0 (protan), normal-vision ΔE 19.6
 *     WARN: yellow 2.11:1 and magenta 2.62:1 against the light surface, below 3:1
 *
 *   dark (#3987e5, #c98500, #d55181, #008300) on #1a1a19 — ALL CHECKS PASS
 *     worst pair CVD ΔE 6.9 (protan), normal-vision ΔE 19.3
 *
 * Eight hues was measured and is not close: red↔orange lands at ΔE 7.1 for NORMAL vision,
 * which is a hard fail — full-colour readers cannot separate them either. Every subset of
 * five or more fails too; four is the ceiling, and only two orderings reach it.
 *
 * So the eight materials are four hues x two dash patterns, and the pair sharing a hue is
 * always separated by the dash. That is composite encoding, not a cycled palette: no two
 * materials share a (hue, dash) slot. Unknown is deliberately outside the categorical set —
 * grey and dotted, because "we could not identify the surface" is an absence, not a category.
 *
 * The light-mode contrast WARN obliges relief: the Surface panel is a real legend, with a
 * swatch showing each material's hue AND dash, and the detail rail names the material of the
 * selected route in text. Keep both if you touch this.
 *
 * Indexed by AU's material order (RtkGeometry.Materials) so a material's colour is a property
 * of the material itself and never of how many are currently on screen — filtering the list
 * must not repaint the survivors.
 */

import type { ExpressionSpecification } from 'maplibre-gl'

/** Must stay in the same order as `RtkGeometry.Materials` on the server. */
export const MATERIAL_ORDER = [
  'Zemlja', 'Plata', 'Flasta', 'Asfalt', 'S. Flasta', 'Mozaik', 'Hansegrand', 'NB',
] as const

const HUES_LIGHT = ['#2a78d6', '#eda100', '#e87ba4', '#008300'] as const
const HUES_DARK = ['#3987e5', '#c98500', '#d55181', '#008300'] as const

/** Outside the categorical set on purpose — see the header. */
const UNKNOWN_LIGHT = '#8a8a83'
const UNKNOWN_DARK = '#9a9a92'

/**
 * Dash class, not a dasharray: MapLibre's `line-dasharray` is one of the few paint
 * properties with no data-driven form, so the pattern cannot come from a feature property.
 * Each class is drawn as its own layer and features are filtered into it.
 */
export const DASH_SOLID = 0
export const DASH_DASHED = 1
export const DASH_DOTTED = 2

/** Zoom-independent; the units are line-widths, so these scale with the line. */
export const DASH_ARRAY: Record<number, number[] | undefined> = {
  [DASH_SOLID]: undefined,
  [DASH_DASHED]: [2, 1.4],
  [DASH_DOTTED]: [0.4, 1.6],
}

const indexOf = (material: string) =>
  (MATERIAL_ORDER as readonly string[]).indexOf(material)

export function materialColor(material: string, dark: boolean): string {
  const i = indexOf(material)
  if (i < 0) return dark ? UNKNOWN_DARK : UNKNOWN_LIGHT
  return (dark ? HUES_DARK : HUES_LIGHT)[i % 4]!
}

export function materialDashClass(material: string): number {
  const i = indexOf(material)
  if (i < 0) return DASH_DOTTED
  return i < 4 ? DASH_SOLID : DASH_DASHED
}

/**
 * A MapLibre `match` over material name, so colour stays a paint expression and one
 * `setPaintProperty` can recolour every route when the selection changes.
 *
 * Built from the same table as `materialColor`, so the legend and the map cannot drift.
 */
export function materialColorExpression(dark: boolean): ExpressionSpecification {
  const cases = MATERIAL_ORDER.flatMap((m) => [m, materialColor(m, dark)])
  // `match` is typed as a variadic tuple, which a spread-built array cannot satisfy without
  // enumerating all eight branches by hand. The shape is correct by construction here.
  return ['match', ['get', 'material'], ...cases,
          dark ? UNKNOWN_DARK : UNKNOWN_LIGHT] as unknown as ExpressionSpecification
}
